import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const DataContext = createContext(null);

const EMPTY_DATA = {
  users: [],
  events: [],
  attendants: [],
  achievements_user: [],
  achievements_details: [],
  all_achievements: [],
  courses: [],
  counts: {
    users: 0,
    events: 0,
    attendants: 0,
    achievements_user: 0,
    achievements_details: 0,
    all_achievements: 0,
    courses: 0,
  },
};

const getResponseArray = (value) =>
  Array.isArray(value) ? value : value?.results ?? value?.data ?? value?.items ?? [];

export function DataProvider({ children }) {
  const auth = useAuth();

  const [state, setState] = useState({
    loading: true,
    error: null,
    data: EMPTY_DATA,
  });

  const fetchData = useCallback(async () => {
    const base = process.env.REACT_APP_API_URL;

    if (!base) {
      const err = new Error("REACT_APP_API_URL is undefined");
      console.error(err);
      setState({
        loading: false,
        error: err,
        data: EMPTY_DATA,
      });
      return;
    }

    setState((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    try {
      const urls = {
        users: `${base}/users`,
        events: `${base}/events`,
        attendants: `${base}/events-attendants`,
        achievements_user: `${base}/users/achievements`,
        achievements_details: `${base}/achievements`,
        all_achievements: `${base}/all-user-achievements`,
        courses: `${base}/courses`,
      };

      const safeGet = (url) =>
        axios.get(url, { withCredentials: true }).catch((err) => {
          console.error("Endpoint failed:", url, err);
          return { data: [] };
        });

      const safeGet2 = (url) =>
        axios.get(url).catch((err) => {
          console.error("Endpoint failed:", url, err);
          return { data: [] };
        });


 
      const [u, e, at, ua, ad, aa, c] = await Promise.all([
        safeGet(urls.users),
        safeGet(urls.events),
        safeGet(urls.attendants),
        safeGet(urls.achievements_user),
        safeGet(urls.achievements_details),
        safeGet(urls.all_achievements),
        safeGet2(urls.courses),
      ]);

      const users = getResponseArray(u.data);
      const events = getResponseArray(e.data);
      const attendants = getResponseArray(at.data);
      const achievements_user = getResponseArray(ua.data);
      const achievements_details = getResponseArray(ad.data);
      const all_achievements = getResponseArray(aa.data);
      const courses = c.data ?? [];


      setState({
        loading: false,
        error: null,
        data: {
          users,
          events,
          attendants,
          achievements_user,
          achievements_details,
          all_achievements,
          courses,
          counts: {
            users: users.length,
            events: events.length,
            attendants: attendants.length,
            achievements_user: achievements_user.length,
            achievements_details: achievements_details.length,
            all_achievements: all_achievements.length,
            courses: Array.isArray(courses)
              ? courses.length
              : Object.keys(courses ?? {}).length,
          },
        },
      });
    } catch (err) {
      console.error("FETCH ERROR", err);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err,
      }));
    }
  }, []);

  useEffect(() => {
    if (!auth.isInitialized) return;

    if (!auth.isAuthenticated) {
      setState({
        loading: false,
        error: null,
        data: EMPTY_DATA,
      });
      return;
    }

    fetchData();
  }, [auth.isInitialized, auth.isAuthenticated, fetchData]);

  const value = useMemo(
    () => ({
      ...state,
      refreshData: fetchData,
    }),
    [state, fetchData]
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) {
    throw new Error("useData must be used within DataProvider");
  }
  return ctx;
}